import { useCallback, useEffect, useState } from 'react';
import api from '../../utils/api';
import { errorText } from '../../utils/errorText';
import * as S from './styles';

/*
 * A specimen's history, newest first, read from specimen_audit_logs.
 *
 * Read-only by construction: the server has no route that changes or removes
 * a row (and the database refuses it). "Verify" asks the server to re-hash
 * this specimen's chain and says whether it still holds.
 */
const ACTION_COLOR = {
  CREATE: 'var(--c-success)', RESERVE: 'var(--c-info)', ASSIGN: 'var(--c-success)', UPDATE: 'var(--c-warning)',
  STORAGE: 'var(--c-purple)', LINK: 'var(--c-info)', ARCHIVE: 'var(--c-text-muted)', RESTORE: 'var(--c-success)',
  VOID: 'var(--c-danger)', STAGE_ADD: 'var(--c-info)', STAGE_UPDATE: 'var(--c-warning)',
  STAGE_STATUS: 'var(--c-info)', STAGE_REMOVE: 'var(--c-danger)', EDGE_ADD: 'var(--c-text-muted)',
  EDGE_REMOVE: 'var(--c-text-muted)',
};

const show = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

export default function AuditTrail({ specimenId, refreshKey, t }) {
  const [rows, setRows] = useState([]);
  const [next, setNext] = useState(null);
  const [error, setError] = useState('');
  const [verdict, setVerdict] = useState(null);
  const [checking, setChecking] = useState(false);

  const load = useCallback((before) => {
    api.get(`/specimens/${specimenId}/audit`, { params: { limit: 50, ...(before ? { before } : {}) } })
      .then((res) => {
        setRows((current) => (before ? [...current, ...res.data.rows] : res.data.rows));
        setNext(res.data.next_before);
        setError('');
      })
      .catch((e) => setError(errorText(e)));
  }, [specimenId]);

  useEffect(() => { load(null); setVerdict(null); }, [load, refreshKey]);

  const verify = async () => {
    setChecking(true);
    try {
      const res = await api.get(`/specimens/${specimenId}/audit/verify`);
      setVerdict(res.data);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setChecking(false);
    }
  };

  return (
    <section style={S.card} aria-labelledby="audit-title">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <h3 id="audit-title" style={S.sectionTitle}>{t('audit.title')}</h3>
        <button type="button" onClick={verify} disabled={checking} style={S.ghostBtn} title={t('audit.tip.verify')}
          data-testid="audit-verify">{checking ? t('audit.verifying') : t('audit.verify')}</button>
      </div>
      <p style={S.hint}>{t('audit.intro')}</p>
      {verdict && (
        <p data-testid="audit-verdict" role="status"
          style={verdict.intact ? { ...S.hint, color: 'var(--c-success-fg)', fontWeight: 600 } : S.warning}>
          {verdict.intact
            ? t('audit.intact', { count: verdict.rows })
            : t('audit.broken', { seq: verdict.problem.seq, problem: verdict.problem.problem })}
        </p>
      )}
      {error && <p style={S.error} role="alert">{error}</p>}
      <ol style={{ listStyle: 'none', padding: 0, margin: '8px 0 0' }} data-testid="audit-rows">
        {rows.map((row) => (
          <li key={row.log_id} style={{ borderTop: '1px solid var(--c-border)', padding: '10px 0' }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
              <span style={{ ...S.mono, fontSize: 12, color: 'var(--c-text-muted-fg)' }}>#{row.seq}</span>
              <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--c-on-accent)', borderRadius: 4,
                padding: '1px 8px', background: ACTION_COLOR[row.action] || 'var(--c-text-muted)' }}>
                {t(`audit.actions.${row.action}`)}
              </span>
              <span style={{ fontSize: 13 }}>
                {row.actor_username || (row.actor_id ? row.actor_id.slice(0, 8) : t('audit.outsideApp', { role: row.actor_db_role }))}
              </span>
              <time dateTime={row.occurred_at} style={{ fontSize: 12, color: 'var(--c-text-muted-fg)' }}>
                {String(row.occurred_at || '').replace('T', ' ').slice(0, 19)}
              </time>
            </div>
            {row.reason && <p style={{ margin: '4px 0 0', fontSize: 13 }}>{t('audit.reason')}: {row.reason}</p>}
            {row.changes && (
              <ul style={{ margin: '4px 0 0', paddingInlineStart: 18, fontSize: 12 }}>
                {Object.entries(row.changes).map(([field, change]) => (
                  <li key={field}>
                    <span style={S.mono}>{field}</span>: <span dir="ltr">{show(change.old)} → {show(change.new)}</span>
                  </li>
                ))}
              </ul>
            )}
            <div style={{ ...S.mono, fontSize: 10, color: 'var(--c-text-faint-fg)', marginTop: 4 }} dir="ltr"
              title={t('audit.tip.hash')}>
              {row.row_hash ? `${row.row_hash.slice(0, 16)}…` : ''}
            </div>
          </li>
        ))}
      </ol>
      {next && (
        <button type="button" onClick={() => load(next)} style={{ ...S.ghostBtn, marginTop: 8 }}
          title={t('audit.tip.more')} data-testid="audit-more">{t('audit.more')}</button>
      )}
    </section>
  );
}
